import { z } from "zod";
import { competenciaSchema, dataSchema } from "./competencia";
import { FORMAS_DE_PAGAMENTO, TIPOS_FINANCEIROS, type TipoFinanceiro } from "./lancamento";
import { LINHAS_DO_DRE, type LinhaDoDre } from "./linha-do-dre";

/** Uma célula como vem da planilha (SheetJS com datas e números crus). */
export type Celula = string | number | Date | boolean | null | undefined;

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const pad = (n: number) => String(n).padStart(2, "0");
const texto = (c: Celula) => (c == null ? "" : c instanceof Date ? "" : String(c).trim());
export const normalizar = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/** Data do Excel (dias desde 1899-12-30) em "AAAA-MM-DD". */
function serialDoExcel(n: number): string | null {
  if (n < 20000 || n > 80000) return null;
  const d = new Date(Date.UTC(1899, 11, 30) + Math.round(n) * 86_400_000);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** "R$ 1.234,56", "-1.234,56", "(1.234,56)", "1234.5", número: centavos com sinal; nulo se não for valor. */
export function lerValor(c: Celula): number | null {
  if (typeof c === "number") return Number.isFinite(c) ? Math.round(c * 100) : null;
  let s = texto(c);
  if (!s || s === "-") return null;
  const negativo = /^\(.*\)$/.test(s) || /^-/.test(s) || /-$/.test(s);
  s = s.replace(/[()R$\s-]/gi, "");
  if (!s) return null;
  // Com vírgula: padrão brasileiro (ponto de milhar). Sem: ponto decimal se tiver até 2 casas.
  let n: number;
  if (s.includes(",")) n = Number(s.replace(/\./g, "").replace(",", "."));
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) n = Number(s.replace(/\./g, ""));
  else n = Number(s);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100) * (negativo ? -1 : 1);
}

/** dd/mm/aaaa, dd/mm/aa, aaaa-mm-dd, data do Excel ou Date: "AAAA-MM-DD". */
export function lerData(c: Celula): string | null {
  if (c instanceof Date) return Number.isNaN(c.getTime()) ? null : `${c.getFullYear()}-${pad(c.getMonth() + 1)}-${pad(c.getDate())}`;
  if (typeof c === "number") return serialDoExcel(c);
  const s = texto(c);
  let m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
  if (m) {
    const ano = m[3]!.length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    const [dia, mes] = [Number(m[1]), Number(m[2])];
    if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
    return `${ano}-${pad(mes)}-${pad(dia)}`;
  }
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

/** "jan/26", "janeiro de 2026", "Jan 2026", "01/2026", "2026-01", data: "AAAA-MM". */
export function lerMes(c: Celula): string | null {
  if (c instanceof Date || typeof c === "number") return lerData(c)?.slice(0, 7) ?? null;
  const s = normalizar(texto(c));
  if (!s) return null;
  let m = s.match(/^(\d{4})-(\d{1,2})$/);
  if (m && Number(m[2]) >= 1 && Number(m[2]) <= 12) return `${m[1]}-${pad(Number(m[2]))}`;
  m = s.match(/^(\d{1,2})[/.-](\d{2}|\d{4})$/);
  if (m && Number(m[1]) >= 1 && Number(m[1]) <= 12) return `${m[2]!.length === 2 ? 2000 + Number(m[2]) : m[2]}-${pad(Number(m[1]))}`;
  m = s.match(/^([a-zç]{3,9})\.?(?:\s*(?:de|\/|-)?\s*)(\d{2}|\d{4})$/);
  if (m) {
    const i = MESES.indexOf(m[1]!.slice(0, 3));
    if (i >= 0) return `${m[2]!.length === 2 ? 2000 + Number(m[2]) : m[2]}-${pad(i + 1)}`;
  }
  const data = lerData(c);
  return data ? data.slice(0, 7) : null;
}

export type FormatoDaPlanilha = "LISTA" | "DRE_MENSAL";

/** Onde está o cabeçalho e que formato a planilha tem: uma linha com 3+ meses é um DRE mensal. */
export function detectarFormato(linhas: Celula[][]): { formato: FormatoDaPlanilha; cabecalho: number } {
  const limite = Math.min(linhas.length, 15);
  for (let i = 0; i < limite; i++) {
    const meses = (linhas[i] ?? []).slice(1).filter((c) => !!lerMes(c)).length;
    if (meses >= 3) return { formato: "DRE_MENSAL", cabecalho: i };
  }
  const primeira = linhas.findIndex((l) => l.filter((c) => texto(c) !== "" || c instanceof Date || typeof c === "number").length >= 2);
  return { formato: "LISTA", cabecalho: Math.max(0, primeira) };
}

// ─── Sugestão de categoria ───────────────────────────────────────────────

/** Totais e subtotais de um DRE pronto: não são lançamento, ficam de fora. */
const TOTAL = /^(total|subtotal|soma|resultado|lucro|prejuizo|margem|ebitda|saldo|receita (bruta|liquida|total)|despesas? (totais|total)|custos? (totais|total))\b/;

const PALAVRAS: [RegExp, string][] = [
  [/desconto|abatimento|bonifica/, "Descontos concedidos"],
  [/\b(das|simples nacional|iss|pis|cofins|imposto)/, "Impostos sobre o faturamento"],
  [/\b(irpj|csll|imposto de renda)/, "IR e CSLL"],
  [/pro.?labore/, "Pró-labore"],
  [/salari|folha|ferias|13|decimo|inss|fgts|vale.?transporte|vale.?refei|encargo/, "Salários e encargos"],
  [/aluguel|condominio|iptu/, "Aluguel"],
  [/energia|luz|agua|saneamento|gas\b/, "Energia e água"],
  [/internet|telefon|celular|sistema|software|assinatura/, "Internet e sistemas"],
  [/contador|contabil|honorario/, "Contador"],
  [/frete|motoboy|correio|entrega|transporte|combustivel/, "Frete"],
  [/material|insumo|zirconia|dissilicato|resina|gesso|porcelana|ceramica|liga|bloco|dente|compra/, "Materiais e insumos"],
  [/terceiriz|fresagem|usinagem|impressao 3d/, "Serviços terceirizados"],
  [/manutencao|conserto|reparo/, "Manutenção de equipamentos"],
  [/taxa.*(cartao|maquin|boleto)|maquininha|cartao/, "Taxas de cartão e boleto"],
  [/juros|tarifa banc|iof|banco/, "Juros e tarifas bancárias"],
  [/rendimento|aplicacao|cdb/, "Rendimentos"],
  [/retirada|distribuicao de lucro|dividendo/, "Retirada dos sócios"],
  [/fresadora|scanner|forno|impressora|equipamento|maquina/, "Compra de equipamentos"],
  [/emprestimo|financiamento|parcela/, "Empréstimo — parcela do principal"],
  [/aporte/, "Aporte e empréstimo recebido"],
  [/receita|faturamento|servico|protese|trabalho|venda|recebimento/, "Serviços de prótese"],
];

export type CategoriaParaSugerir = { id: string; nome: string; tipo: TipoFinanceiro };
export type Destino = { acao: "ignorar" } | { acao: "categoria"; id: string } | { acao: "criar"; tipo: TipoFinanceiro; linha: LinhaDoDre };

/** O destino provável de um rótulo da planilha: total ⇒ ignorar; nome igual ou palavra-chave ⇒ categoria. */
export function sugerirDestino(rotulo: string, categorias: CategoriaParaSugerir[]): Destino {
  const s = normalizar(rotulo);
  if (!s || TOTAL.test(s)) return { acao: "ignorar" };
  const igual = categorias.find((c) => normalizar(c.nome) === s);
  if (igual) return { acao: "categoria", id: igual.id };
  for (const [re, nome] of PALAVRAS) {
    if (!re.test(s)) continue;
    const c = categorias.find((x) => x.nome === nome);
    if (c) return { acao: "categoria", id: c.id };
  }
  return { acao: "criar", tipo: "SAIDA", linha: "ADMINISTRATIVA" };
}

// ─── Linhas importáveis ──────────────────────────────────────────────────

/** Uma linha pronta para importar (antes de escolher a categoria). */
export type LinhaLida = {
  linhaDaPlanilha: number;
  rotulo: string;
  competencia: string | null;
  descricao: string;
  valorCents: number | null;
  pagoEm: string | null;
  forma: string | null;
  parceiro: string | null;
};

export const CAMPOS_DA_LISTA = [
  { key: "data", label: "Data" },
  { key: "descricao", label: "Descrição" },
  { key: "valor", label: "Valor" },
  { key: "categoria", label: "Categoria" },
  { key: "pagoEm", label: "Pago em" },
  { key: "forma", label: "Forma de pagamento" },
  { key: "parceiro", label: "Fornecedor ou cliente" },
] as const;
export type CampoDaLista = (typeof CAMPOS_DA_LISTA)[number]["key"];

const FORMAS: Record<string, string> = { pix: "PIX", boleto: "BOLETO", cartao: "CARTAO", credito: "CARTAO", debito: "CARTAO", dinheiro: "DINHEIRO", especie: "DINHEIRO", transferencia: "TRANSFERENCIA", ted: "TRANSFERENCIA", doc: "TRANSFERENCIA" };
const lerForma = (c: Celula) => {
  const s = normalizar(texto(c));
  return Object.entries(FORMAS).find(([k]) => s.includes(k))?.[1] ?? null;
};

/** Lista de lançamentos: colunas mapeadas (índice da coluna por campo). Sem "pago em", `jaPagos` usa a data. */
export function linhasDaLista(linhas: Celula[][], cabecalho: number, colunas: Partial<Record<CampoDaLista, number>>, jaPagos: boolean): LinhaLida[] {
  const col = (l: Celula[], k: CampoDaLista) => (colunas[k] == null ? undefined : l[colunas[k]!]);
  return linhas.slice(cabecalho + 1).flatMap((l, i) => {
    if (!l.some((c) => texto(c) !== "" || c instanceof Date || typeof c === "number")) return [];
    const data = lerData(col(l, "data"));
    const descricao = texto(col(l, "descricao")) || texto(col(l, "categoria")) || "Importado";
    const pagoEm = colunas.pagoEm != null ? lerData(col(l, "pagoEm")) : jaPagos ? data : null;
    return [
      {
        linhaDaPlanilha: cabecalho + i + 2,
        rotulo: texto(col(l, "categoria")) || descricao,
        competencia: data?.slice(0, 7) ?? null,
        descricao: descricao.slice(0, 200),
        valorCents: lerValor(col(l, "valor")),
        pagoEm,
        forma: colunas.forma != null ? lerForma(col(l, "forma")) : null,
        parceiro: texto(col(l, "parceiro")) || null,
      },
    ];
  });
}

/** DRE mensal: cada célula (rótulo × mês) vira um lançamento do mês. Pago no último dia, se `jaPagos`. */
export function linhasDoDreMensal(linhas: Celula[][], cabecalho: number, colunaDoRotulo: number, colunasDeMes: number[], jaPagos: boolean): LinhaLida[] {
  const meses = colunasDeMes.map((c) => ({ coluna: c, mes: lerMes(linhas[cabecalho]?.[c]) }));
  return linhas.slice(cabecalho + 1).flatMap((l, i) => {
    const rotulo = texto(l[colunaDoRotulo]);
    if (!rotulo) return [];
    return meses.flatMap(({ coluna, mes }) => {
      const valorCents = lerValor(l[coluna]);
      if (!valorCents || !mes) return [];
      const [a, m] = mes.split("-").map(Number);
      const ultimo = new Date(Date.UTC(a!, m!, 0)).getUTCDate();
      return [{ linhaDaPlanilha: cabecalho + i + 2, rotulo, competencia: mes, descricao: `Importado: ${rotulo}`.slice(0, 200), valorCents, pagoEm: jaPagos ? `${mes}-${pad(ultimo)}` : null, forma: null, parceiro: null }];
    });
  });
}

// ─── API ─────────────────────────────────────────────────────────────────

export const FORMATOS_DA_PLANILHA = ["LISTA", "DRE_MENSAL"] as const;

export const importacaoIniciarSchema = z.object({
  arquivo: z.string().trim().min(1).max(200),
  formato: z.enum(FORMATOS_DA_PLANILHA),
  /** Categorias a criar, com a chave que as linhas usam para apontar para elas. */
  novasCategorias: z.array(z.object({ chave: z.string().min(1).max(200), nome: z.string().trim().min(2).max(60), tipo: z.enum(TIPOS_FINANCEIROS), linhaDoDre: z.enum(LINHAS_DO_DRE) })).max(300),
});

export const linhaParaImportarSchema = z.object({
  competencia: competenciaSchema,
  descricao: z.string().trim().min(1).max(200),
  valorCents: z.number().int().positive(),
  categoriaId: z.string().min(1),
  pagoEm: dataSchema.nullable(),
  formaDePagamento: z.enum(FORMAS_DE_PAGAMENTO).nullable(),
  parceiro: z.string().trim().max(200).nullable(),
});
export const importacaoLoteSchema = z.object({ importacaoId: z.string().min(1), linhas: z.array(linhaParaImportarSchema).min(1).max(500) });
export const importacaoPreviaSchema = z.object({
  meses: z.array(competenciaSchema).max(240),
  candidatos: z.array(z.object({ competencia: competenciaSchema, valorCents: z.number().int(), descricao: z.string().max(200) })).max(20_000),
});
export type LinhaParaImportar = z.infer<typeof linhaParaImportarSchema>;
